import { Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { SidebarProvider } from './contexts/SidebarContext';
import ProtectedRoute from './components/ProtectedRoute';
import { AdminLayout } from './components/Layout';
import AdminLoginPage from './pages/AdminLoginPage';
import AdminDashboardPage from './pages/AdminDashboardPage';
import { ZipMasterSeedPage } from './pages/Reference/ZipMasterSeedPage';
import { ZipMasterListPage } from './pages/Reference/ZipMasterListPage';
import { UsersListPage } from './pages/Users/UsersListPage';
import { UserDetailPage } from './pages/Users/UserDetailPage';
import { SqlEditorPage } from './pages/System/SqlEditorPage';
import { ModerationQueuePage } from './pages/Moderation/ModerationQueuePage';
import { FlaggedMessagesPage } from './pages/Moderation/FlaggedMessagesPage';
import { CourtsPage } from './pages/Reference/CourtsPage';
import { FeedManagementPage } from './pages/Content/FeedManagementPage';
import { DynamoDbEditorPage } from './pages/System/DynamoDbEditorPage';
import { CompliancePage } from './pages/System/CompliancePage';
import { ApiDocsPage } from './pages/System/ApiDocsPage';
import { AuditLogPage } from './pages/System/AuditLogPage';
import { ProviderReviewPage } from './pages/Providers/ProviderReviewPage';

// Placeholder page component for routes not yet implemented
function PlaceholderPage({ title }: { title: string }) {
  return (
    <div className="p-6">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-2xl font-bold text-white mb-2">{title}</h1>
        <p className="text-slate-400">This page is under construction.</p>
        <div className="mt-8 p-8 border border-dashed border-slate-700 rounded-lg text-center text-slate-500">
          Coming soon...
        </div>
      </div>
    </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <SidebarProvider>
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={<AdminLoginPage />} />

          {/* Protected routes with layout */}
          <Route
            element={
              <ProtectedRoute>
                <AdminLayout />
              </ProtectedRoute>
            }
          >
            {/* Dashboard */}
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/dashboard" element={<AdminDashboardPage />} />

            {/* Users */}
            <Route path="/users" element={<UsersListPage />} />
            <Route path="/users/:id" element={<UserDetailPage />} />
            <Route path="/users/verification" element={<PlaceholderPage title="User Verification" />} />
            <Route path="/users/blocked" element={<PlaceholderPage title="Blocked Users" />} />
            <Route path="/users/reports" element={<PlaceholderPage title="User Reports" />} />

            {/* Admin Users */}
            <Route path="/admins" element={<PlaceholderPage title="Admin Accounts" />} />
            <Route path="/admins/roles" element={<PlaceholderPage title="Roles & Permissions" />} />

            {/* Squads */}
            <Route path="/squads" element={<PlaceholderPage title="All Squads" />} />
            <Route path="/squads/memberships" element={<PlaceholderPage title="Squad Memberships" />} />
            <Route path="/squads/moderation" element={<PlaceholderPage title="Squad Moderation" />} />

            {/* Sessions */}
            <Route path="/sessions" element={<PlaceholderPage title="Scheduled Sessions" />} />
            <Route path="/sessions/active" element={<PlaceholderPage title="Active Sessions" />} />
            <Route path="/sessions/history" element={<PlaceholderPage title="Session History" />} />

            {/* Matches */}
            <Route path="/matches" element={<PlaceholderPage title="Match History" />} />
            <Route path="/matches/live" element={<PlaceholderPage title="Live Matches" />} />

            {/* Messages */}
            <Route path="/messages/squads" element={<PlaceholderPage title="Squad Chats" />} />
            <Route path="/messages/dms" element={<PlaceholderPage title="Direct Messages" />} />
            <Route path="/messages/flagged" element={<FlaggedMessagesPage />} />

            {/* Moderation */}
            <Route path="/moderation/queue" element={<ModerationQueuePage />} />
            <Route path="/moderation/actions" element={<PlaceholderPage title="Moderation Actions" />} />

            {/* Providers (Academy) */}
            <Route path="/providers/review" element={<ProviderReviewPage />} />
            <Route path="/providers/coaches" element={<ProviderReviewPage initialType="COACH" />} />
            <Route path="/providers/academies" element={<ProviderReviewPage initialType="ACADEMY" />} />
            <Route path="/providers/stringers" element={<ProviderReviewPage initialType="STRINGER" />} />
            <Route path="/certifications" element={<PlaceholderPage title="Certifications" />} />

            {/* Buzz (Content) */}
            <Route path="/buzz/articles" element={<PlaceholderPage title="Articles" />} />
            <Route path="/buzz/events" element={<PlaceholderPage title="Events" />} />
            <Route path="/buzz/promos" element={<PlaceholderPage title="Promos" />} />
            <Route path="/content/feed" element={<FeedManagementPage />} />

            {/* Reference Data */}
            <Route path="/reference/zip-master" element={<ZipMasterListPage />} />
            <Route path="/reference/zip-master/seed" element={<ZipMasterSeedPage />} />
            <Route path="/reference/courts" element={<CourtsPage />} />
            <Route path="/reference/play-formats" element={<PlaceholderPage title="Play Formats" />} />

            {/* System */}
            <Route path="/system/sql-editor" element={<SqlEditorPage />} />
            <Route path="/system/dynamodb" element={<DynamoDbEditorPage />} />
            <Route path="/system/audit-log" element={<AuditLogPage />} />
            <Route path="/system/api-docs" element={<ApiDocsPage />} />
            <Route path="/system/compliance" element={<CompliancePage />} />
            <Route path="/system/health" element={<PlaceholderPage title="System Health" />} />
            <Route path="/system/jobs" element={<PlaceholderPage title="Background Jobs" />} />
            <Route path="/system/push" element={<PlaceholderPage title="Push Notifications" />} />
            <Route path="/system/logs" element={<PlaceholderPage title="System Logs" />} />
            <Route path="/system/settings" element={<PlaceholderPage title="Settings" />} />

            {/* Legacy route redirect */}
            <Route path="/zip-master" element={<Navigate to="/reference/zip-master" replace />} />
          </Route>

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </SidebarProvider>
    </AuthProvider>
  );
}
